import { of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Patient, MatchCandidate, PatientFormsContext, PatientFormsEvent } from './patient-forms.types';
import { PatientLinkApiService } from '../services/patient-link-api.service';
import { DobFormatService } from '../services/dob-format.service';
import { mapSearchItemToPatient } from '../services/patient-mapping';

type SendBack = (event: PatientFormsEvent) => void;

export const checkCrossSystemMatches = (
	api: PatientLinkApiService,
	context: PatientFormsContext,
	dobFormat: DobFormatService
): ((sendBack: SendBack) => void) => {
	const pending = context.pendingIteroPatient || context.pendingInvisalignPatient;

	if (!pending) {
		return sendBack => {
			sendBack({ type: 'PATIENT_MATCH.NO_MATCHES' });
		};
	}

	const searchingInvisalign = pending.system === 'iTero';

	// iTero patient with toggle OFF → no need to search for Invisalign matches
	if (searchingInvisalign && !context.invisalignToggle) {
		return sendBack => {
			sendBack({ type: 'PATIENT_MATCH.NO_MATCHES' });
		};
	}

	const source = searchingInvisalign ? 'Invisalign' : 'ITero';

	return sendBack => {
		const sub = api
			.searchPatients(pending.firstName, pending.lastName, source)
			.pipe(
				map(res => {
					const patients = (res.results ?? []).map(item => mapSearchItemToPatient(item)).filter(p => p.system !== 'both');

					return searchingInvisalign ? filterInvisalignCandidates(patients, pending, dobFormat) : patients;
				}),
				catchError(() => of(null))
			)
			.subscribe(patients => {
				if (patients === null) {
					sendBack({ type: 'PATIENT_MATCH.ERROR' });
				} else if (patients.length === 0) {
					sendBack({ type: 'PATIENT_MATCH.NO_MATCHES' });
				} else {
					const matches: MatchCandidate[] = patients.map(p => ({ patient: p, wouldCreateDuplicate: false }));
					const canCreateNew = searchingInvisalign
						? !hasExactInvisalignDuplicate(patients, pending, dobFormat)
						: !hasExactIteroDuplicate(patients, pending);

					sendBack({ type: 'PATIENT_MATCH.MATCHES_FOUND', matches, canCreateNew });
				}
			});

		return () => sub.unsubscribe();
	};
};

const filterInvisalignCandidates = (candidates: Patient[], pending: Patient, dobFormat: DobFormatService): Patient[] =>
	candidates.filter(c => {
		if (pending.dateOfBirth && c.dateOfBirth && !dobFormat.isSameDoB(pending.dateOfBirth, c.dateOfBirth)) {
			return false;
		}

		return true;
	});

const hasExactInvisalignDuplicate = (candidates: Patient[], pending: Patient, dobFormat: DobFormatService): boolean =>
	candidates.some(c => dobFormat.isSameDoB(c.dateOfBirth, pending.dateOfBirth));

const hasExactIteroDuplicate = (candidates: Patient[], pending: Patient): boolean => {
	if (!pending.chartNumber) {
		return false;
	}

	return candidates.some(c => c.chartNumber && c.chartNumber.toLowerCase() === pending.chartNumber.toLowerCase());
};
